import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const LightboxContent = styled.div`
  position: relative;
  width: 90%;
  height: 90%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const LightboxImageContainer = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
`;

const LightboxControls = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  z-index: 1001;
  pointer-events: none; /* This allows clicks to pass through to the image */
`;

const NavButton = styled.button`
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  pointer-events: auto; /* Re-enable pointer events for the buttons */
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);

  &:hover {
    background-color: rgba(44, 125, 70, 0.8);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  z-index: 1002;
  
  &:hover {
    background-color: rgba(170, 0, 0, 0.8);
  }
`;

const ImageInfo = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 12px;
  border-radius: 4px;
  margin-top: 10px;
  text-align: center;
  max-width: 800px;
  width: 100%;
`;

const ImageTitle = styled.h3`
  margin: 0 0 5px 0;
  font-size: 1.4rem;
`;

const ImageDetails = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 5px;
  flex-wrap: wrap;
  font-size: 0.9rem;
`;

const ImageDetail = styled.div`
  font-size: 0.9rem;
  color: #ddd;
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 10px;
  width: 100%;
  overflow-x: auto;
  padding: 5px 0;
  max-height: 80px;

  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
  }
`;

const Thumbnail = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#2c7d46' : 'transparent'};
  opacity: ${props => props.active ? 1 : 0.6};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

function ImageLightbox({ images, initialIndex = 0, onClose, visible }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isVisible, setIsVisible] = useState(false);

  // Update currentIndex when initialIndex changes (when a new image is clicked)
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Set initial visibility based on prop
  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isVisible) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case 'ArrowRight':
        setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : prev));
        break;
      case 'Escape':
        if (onClose) onClose();
        break;
      default:
        break;
    }
  }, [isVisible, images, onClose]);
  
  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // Navigate to previous image
  const goToPrevious = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
  };
  
  // Navigate to next image
  const goToNext = () => {
    setCurrentIndex(prev => (prev < images.length - 1 ? prev + 1 : prev));
  };
  
  // Get current image
  const currentImage = images[currentIndex];
  
  // Handle click on background to close
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      if (onClose) onClose();
    }
  };
  
  // Handle thumbnail click
  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };
  
  if (!images || images.length === 0 || !currentImage) {
    return null;
  }
  
  return (
    <LightboxOverlay visible={isVisible} onClick={handleBackgroundClick}>
      <CloseButton onClick={onClose}>×</CloseButton>
      
      <LightboxContent>
        <LightboxImageContainer>
          <LightboxImage 
            src={currentImage.src} 
            alt={currentImage.title || `Image ${currentIndex + 1}`} 
          />
        </LightboxImageContainer>
        
        {currentImage.title && (
          <ImageInfo>
            <ImageTitle>{currentImage.title}</ImageTitle>
            
            <ImageDetails>
              {currentImage.medium && (
                <ImageDetail><strong>Medium:</strong> {currentImage.medium}</ImageDetail>
              )}
              {currentImage.size && (
                <ImageDetail><strong>Size:</strong> {currentImage.size}</ImageDetail>
              )}
              {currentImage.year && (
                <ImageDetail><strong>Year:</strong> {currentImage.year}</ImageDetail>
              )}
              {currentImage.available !== undefined && (
                <ImageDetail>
                  <strong>Status:</strong> {currentImage.available ? 'Available' : currentImage.status || 'Not Available'}
                </ImageDetail>
              )}
              {currentImage.price && currentImage.available && (
                <ImageDetail><strong>Price:</strong> ${currentImage.price.toLocaleString()}</ImageDetail>
              )}
            </ImageDetails>
          </ImageInfo>
        )}
        
        {images.length > 1 && (
          <ThumbnailsContainer>
            {images.map((image, index) => (
              <Thumbnail 
                key={index} 
                active={index === currentIndex}
                onClick={() => handleThumbnailClick(index)}
              >
                <ThumbnailImage 
                  src={image.src} 
                  alt={`Thumbnail ${index + 1}`}
                />
              </Thumbnail>
            ))}
          </ThumbnailsContainer>
        )}
        
        <LightboxControls>
          <NavButton
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            aria-label="Previous image"
          >
            ←
          </NavButton>
          <NavButton
            onClick={goToNext}
            disabled={currentIndex === images.length - 1}
            aria-label="Next image"
          >
            →
          </NavButton>
        </LightboxControls>
      </LightboxContent>
    </LightboxOverlay>
  );
}

export default ImageLightbox;